<template>
  <div
    id="kt_aside_menu_wrapper"
    ref="scrollElRef"
    class="hover-scroll-overlay-y"
    data-kt-scroll="true"
    data-kt-scroll-activate="{default: false, lg: true}"
    data-kt-scroll-dependencies="#kt_aside_logo"
    data-kt-scroll-height="auto"
    data-kt-scroll-offset="0"
    data-kt-scroll-wrappers="#kt_aside_menu"
  >
    <ul
      id="menu"
      class="sidebar-nav"
      role="navigation"
      aria-label="Main navigation"
    >
      <template
        v-for="(item, i) in MenuConfig"
        :key="i"
      >
        <!-- Category heading -->
        <li
          v-if="item.heading"
          class="menu-item"
        >
          <div class="menu-content pt-8 pb-2">
            <span class="menu-section text-muted text-uppercase fs-8 ls-1">
              {{ translate(item.heading) }}
            </span>
          </div>
        </li>

        <template
          v-for="(menuItem, j) in item.pages"
          :key="j"
        >
          <!-- Elements without submenu -->
          <li
            v-if="menuItem.heading && !menuItem.sub"
            class="menu-item"
          >
            <router-link
              v-slot="{ href, navigate, isActive, isExactActive }"
              :to="menuItem.route"
            >
              <a
                :class="[
                  (isActive || isRouteActive(menuItem.route)) && 'active',
                  isExactActive && 'active',
                  menuItem.heading === 'aside.menu.pages.userAssets' && 'menu-link--user-assets'
                ]"
                :href="href"
                :data-title="translate(menuItem.heading)"
                @click="navigate"
              >
                <span class="menu-icon fa-stack">
                  <i
                    :class="menuItem.fontIcon"
                    class="bi"
                  />
                  <div
                    v-if="menuItem.badge"
                    class="badge2"
                  >
                    {{ menuItem.badge }}
                  </div>
                </span>
                <span class="menu-title">
                  {{ translate(menuItem.heading) }}
                  <span
                    v-if="menuItem.badgeInline"
                    class="badge badge-primary ms-1"
                  >
                    {{ menuItem.badgeInline }}
                  </span>
                </span>
              </a>
            </router-link>
          </li>

          <!-- Elements with submenu -->
          <li
            v-else-if="menuItem.heading && menuItem.sub"
            :class="[
              'menu-item',
              'menu-accordion',
              menuItem.heading === 'aside.menu.pages.assets' && 'menu-accordion--assets',
              { 'show': hasActiveChildren(menuItem.route, menuItem.sub) }
            ]"
            data-kt-menu-sub="accordion"
          >
            <span
              class="menu-link"
              @click="toggleSubmenu($event)"
            >
              <span class="menu-icon fa-stack">
                <i
                  :class="menuItem.fontIcon"
                  class="bi"
                />
              </span>
              <router-link
                v-if="menuItem.route"
                :to="menuItem.route"
                class="menu-title"
                :class="{ active: isRouteActive(menuItem.route) }"
              >
                {{ translate(menuItem.heading) }}
              </router-link>
              <span
                v-else
                class="menu-title"
              >{{ translate(menuItem.heading) }}</span>
              <span class="menu-arrow" />
            </span>
            <ul
              :class="{ show: hasActiveChildren(menuItem.route, menuItem.sub) }"
              class="menu-sub menu-sub-accordion"
            >
              <li
                v-for="(item2, k) in menuItem.sub"
                :key="k"
                class="menu-item"
              >
                <router-link
                  v-if="item2.heading"
                  :to="item2.route"
                  :class="{ active: isExactRouteActive(item2.route) }"
                >
                  <span class="menu-bullet">
                    <span class="bullet bullet-dot" />
                  </span>
                  <span class="menu-title">{{ translate(item2.heading) }}</span>
                </router-link>
              </li>
            </ul>
          </li>
        </template>
      </template>

      <!-- Separator at the end -->
      <li class="menu-item">
        <div class="menu-content">
          <div class="separator mx-1 my-4" />
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import {defineComponent, onMounted, ref, computed, watch, nextTick} from 'vue'
import {useI18n} from 'vue-i18n'
import {useRoute} from 'vue-router'
import {ScrollComponent} from '@/assets/ts/components/_ScrollComponent'
import {MenuComponent} from '@/assets/ts/components/MenuComponent'
import MenuConfig from '@/core/config/MenuConfig'
import MenuModules from '@/core/config/MenuModules'
import {canAccessAssetModule, userDetails} from "@/utils/tools";
import { hasWirepas, fetchWirepasStatus } from '@/stores/wirepasStore';

export default defineComponent({
  name: 'Menu',
  setup() {
    const {t, te} = useI18n()
    const route = useRoute()
    const scrollElRef = ref<null | HTMLElement>(null)
    const refreshKey = ref(0)

    const assetRoutes = new Set([
      '/assets',
      '/assets/groups',
      '/assets-map',
      '/asset-hierarchy',
      '/asset-groups',
      '/asset-groups/with-location',
      '/asset-schemes',
      '/wirepas-locations',
      '/wirepas-assets',
      '/notifications/asset-rules',
      '/notifications/asset-history',
      '/user-assets'
    ])

    const allowedSubmenuRoutesForAssetRole = new Set([
      '/notifications/asset-rules',
      '/notifications/asset-history',
      '/asset-groups',
      '/asset-groups/with-location',
      '/asset-schemes',
      '/wirepas-locations',
      '/wirepas-assets',
      '/infrastructure',
      '/assets',
      '/assets/groups',
      '/assets/modbus-params',
      '/user-assets'
    ])

    const collapseInactiveSubmenus = () => {
      const menuRoot = scrollElRef.value
      if (!menuRoot) return
      const items = menuRoot.querySelectorAll('.menu-accordion')
      items.forEach((item: Element) => {
        const hasActive = item.querySelector('.menu-sub-accordion a.active')
        if (!hasActive) {
          item.classList.remove('show')
          const submenu = item.querySelector('.menu-sub-accordion')
          if (submenu) {
            submenu.classList.remove('show')
          }
        }
      })
    }

    // Watch route changes to refresh menu (triggers computed recalculation)
    watch(() => route.path, () => {
      refreshKey.value++
      nextTick(() => collapseInactiveSubmenus())
    })

    onMounted(() => {
      fetchWirepasStatus()
      ScrollComponent.reinitialization()
      MenuComponent.reinitialization()
      if (scrollElRef.value) {
        scrollElRef.value.scrollTop = 0
      }
      nextTick(() => collapseInactiveSubmenus())
    })

    const translate = text => {
      if (te(text)) {
        return t(text)
      } else {
        return text
      }
    }

    const isRouteActive = (menuRoute: string): boolean => {
      if (!menuRoute) return false
      // Check if current path starts with menu route (handles child routes like /assets/:id)
      return route.path === menuRoute || route.path.startsWith(menuRoute + '/')
    }

    const isExactRouteActive = (menuRoute: string): boolean => {
      if (!menuRoute) return false
      return route.path === menuRoute
    }

    const hasActiveChildren = (match: string | undefined, submenuItems: any[] | null = null): boolean => {
      // If submenu items are provided, check if any child route is active
      if (submenuItems && Array.isArray(submenuItems)) {
        return submenuItems.some((item: any) => {
          if (!item.route) return false
          return route.path === item.route || route.path.startsWith(item.route + '/')
        })
      }

      // Fallback to original logic
      if (!match) return false
      return route.path.startsWith(match)
    }

    const toggleSubmenu = (event) => {
      const menuItem = event.target.closest('.menu-accordion')
      if (menuItem) {
        menuItem.classList.toggle('show')
        const submenu = menuItem.querySelector('.menu-sub-accordion')
        if (submenu) {
          submenu.classList.toggle('show')
        }
      }
    }

    // Computed property to reactively filter MenuConfig based on user roles
    const filteredMenuConfig = computed(() => {
      // Include refreshKey to trigger recomputation on route changes
      refreshKey.value;
      hasWirepas.value; // triggers recompute after async fetch

      const userDetailsData = userDetails();

      if (userDetailsData === null) {
        return MenuConfig;
      }

      const filteredConfig = JSON.parse(JSON.stringify(MenuConfig)); // Deep clone to avoid mutating original

      const hasAsset = canAccessAssetModule(userDetailsData.roles);
      // Full superadmin (ROLE_ADMIN + ROLE_BOSS) gets the unrestricted menu, even though
      // canAccessAssetModule() also returns true for them -- that helper answers "can read
      // asset data", not "is limited to asset data". See matching fix in router/index.ts.
      const isSuperAdmin = userDetailsData.roles.includes('ROLE_ADMIN') && userDetailsData.roles.includes('ROLE_BOSS');
      const userParameters: string[] = (Array.isArray(userDetailsData?.parameters) ? userDetailsData.parameters : [])
        .map((p: any) => p.name);
      const hasRequiredRole = (roles?: string[]) => {
        if (!Array.isArray(roles)) return true;
        return roles.some(role => userDetailsData.roles.includes(role))
          || (roles.includes('ROLE_ASSET') && canAccessAssetModule(userDetailsData.roles));
      };

      filteredConfig.forEach(category => {
        category.pages = category.pages
            .map(page => {
              if (page.wirepasOnly && !hasWirepas.value) return null;

              let hasAccessToPage;

              // Logic based on user roles:
              // 1. User with ROLE_ASSET - only asset-related paths
              // 2. User without ROLE_ASSET - everything except asset-related paths

              if (isSuperAdmin) {
                // Full superadmin: not restricted to either the asset or the non-asset subset
                hasAccessToPage = hasRequiredRole(page.roles);
              } else if (hasAsset) {
                // User with ROLE_ASSET: only asset-related routes
                hasAccessToPage = assetRoutes.has(page.route);
              } else {
                // User without ROLE_ASSET: everything except asset-related routes
                // Block asset-related routes
                if (assetRoutes.has(page.route)) {
                  hasAccessToPage = false;
                }
                // Block Asset Groups menu (parent item without route)
                else if (page.heading === 'aside.menu.pages.assetGroups') {
                  hasAccessToPage = false;
                }
                else {
                  hasAccessToPage = hasRequiredRole(page.roles);
                }
              }

              const filteredPage = {...page};

              if (page.sub && Array.isArray(page.sub)) {
                if (isSuperAdmin) {
                  filteredPage.sub = page.sub.filter(subItem => {
                    if (!hasRequiredRole(subItem.roles)) return false;
                    if (subItem.parameters && !subItem.parameters.some(p => userParameters.includes(p))) return false;
                    return true;
                  });
                } else if (hasAsset) {
                  // Allow asset notification rules, asset notification history and asset groups submenu for ROLE_ASSET users
                  filteredPage.sub = page.sub.filter(subItem => {
                    if (!allowedSubmenuRoutesForAssetRole.has(subItem.route)) return false;
                    if (subItem.route === '/infrastructure') {
                      // Company-privileged sub-route: needs asset owner/admin specifically --
                      // hasRequiredRole's plain OR match on ROLE_BOSS alone isn't precise enough
                      // here (a ROLE_BOSS+ROLE_ASSET user without ROLE_ADMIN must not see it).
                      return userDetailsData.roles.includes('ROLE_ASSET_OWNER') || userDetailsData.roles.includes('ROLE_ASSET_ADMIN');
                    }
                    if (!hasRequiredRole(subItem.roles)) return false;
                    if (subItem.parameters && !subItem.parameters.some(p => userParameters.includes(p))) return false;
                    return true;
                  });
                } else {
                  // For users without ROLE_ASSET, block asset-related submenu items
                  filteredPage.sub = page.sub.filter(subItem => {
                    if (allowedSubmenuRoutesForAssetRole.has(subItem.route)) return false;
                    if (!hasRequiredRole(subItem.roles)) return false;
                    if (subItem.parameters && !subItem.parameters.some(p => userParameters.includes(p))) return false;
                    return true;
                  });
                }
              }
              const hasAccessibleSubmenu = Array.isArray(filteredPage.sub) && filteredPage.sub.length > 0;
              const isSubmenuOnlyPage = Array.isArray(page.sub) && !page.route;
              return (hasAccessibleSubmenu || (hasAccessToPage && !isSubmenuOnlyPage)) ? filteredPage : null;
            })
            .filter(page => page !== null);
      });

      //Menu modules with roles
      // Don't load modules for ROLE_ASSET users (but superadmins always see them)
      if (!hasAsset || isSuperAdmin) {
        const userParameters = Array.isArray(userDetailsData?.parameters)
            ? userDetailsData.parameters
            : [];

        const modules = userDetailsData?.modules || [];
        modules.forEach(module => {
          const moduleConfig = MenuModules[module.id];

          if (moduleConfig && (!moduleConfig.roles || moduleConfig.roles.some(role => userDetailsData.roles.includes(role)))) {
            const filteredModule = {...moduleConfig, sub: []};

            if (Array.isArray(moduleConfig.sub)) {
              filteredModule.sub = moduleConfig.sub.filter(subItem => {
                if (subItem.roles && !subItem.roles.some(role => userDetailsData.roles.includes(role))) {
                  return false;
                }

                if (!Object.prototype.hasOwnProperty.call(subItem, 'parameters')) {
                  return true;
                }

                if (Array.isArray(subItem.parameters)) {
                  return subItem.parameters.some(requiredParam =>
                      userParameters.some(userParam =>
                          userParam.name === requiredParam
                      )
                  );
                }
                return false;
              });
            }

            if (filteredModule.sub.length > 0 || !moduleConfig.roles) {
              const moduleExists = filteredConfig[0].pages.some(page =>
                page.heading === filteredModule.heading && page.fontIcon === filteredModule.fontIcon
              );
              if (!moduleExists) {
                filteredConfig[0].pages.push(filteredModule);
              }
            }
          }
        });
      }

      return filteredConfig;
    });

    return {
      isRouteActive,
      isExactRouteActive,
      hasActiveChildren,
      toggleSubmenu,
      MenuConfig: filteredMenuConfig,
      translate
    }
  }
})
</script>
